/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */

import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-lb-checkbox',
    template: `
        <div class="form-group" [formGroup]="formGroup">
            <div class="form-content">
                <div class="checkbox-wrapper">
                    <input
                        [title]="formControlName"
                        class="lb-checkbox"
                        [id]="checkboxId ? checkboxId : formControlName"
                        type="checkbox"
                        [name]="formControlName"
                        [formControlName]="formControlName"/>
                    <label [for]="formControlName" class="lb-checkbox">{{ label }}</label>
                </div>
                <div [hidden]="hideValidation" class="validation-wrapper">
                    <app-validation-messages [control]="formGroup.get(formControlName)"></app-validation-messages>
                </div>
                <app-help *ngIf="helpTag" [tag]="helpTag"></app-help>
            </div>
        </div>
        <div class="checkbox-content">
            <div [hidden]="!isChecked() && !showHiddenContent">
                <ng-content></ng-content>
            </div>
        </div>`,
    styleUrls: ['./checkbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CheckboxComponent),
        }
    ]
})

export class CheckboxComponent implements ControlValueAccessor {
    /**
     * Our form group
     */
    @Input() public formGroup: FormGroup = new FormGroup({
        default: new FormControl('')
    });

    /**
     * Our form control
     */
    @Input() public formControlName: string = 'default';

    /**
     * Set an ID on a form if there are multiple checkboxes with the same form name.
     */
    @Input() public checkboxId: string;

    /**
     * Our label for the checkbox
     */
    @Input() public label: string = 'N/A';

    /**
     * Our help tage for the checkbox
     */
    @Input() public helpTag: string;

    /**
     * Show the content
     */
    @Input() public showHiddenContent: boolean = false;

    /**
     * Allows parent component to control the validation visibility.
     */
    @Input() public hideValidation: boolean = false;

    /**
     * Event handler for when the view changes
     * // Note: Required by implementation. Do not remove.
     * @param _fn
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public registerOnChange(_fn: any): void {
    }

    /**
     * Register a function to determine if the input has been touched
     * // Note: Required by implementation. Do not remove.
     * @param _fn
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public registerOnTouched(_fn: any): void {
    }

    /**
     * Write value to input
     * // Note: Required by implementation. Do not remove.
     * @param _val
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public writeValue(_val: any): void {
    }

    /**
     * Check if our checkbox is checked
     */
    public isChecked(): boolean {
        return this.formGroup.controls[this.formControlName].value;
    }
}
